import { Player } from "textalive-app-api";
import { DEFAULT_SONG_ID, findSongByUrl, SONG_BY_ID } from "../config/songs.js";
import { Emitter } from "./events.js";

const TOKEN_PLACEHOLDER = "YOUR_TEXTALIVE_APP_TOKEN";

export class TextAliveController extends Emitter {
  constructor({ mediaElement, initialSongId = DEFAULT_SONG_ID }) {
    super();
    this.mediaElement = mediaElement;
    this.selectedSongId = initialSongId;
    this.currentSong = SONG_BY_ID[initialSongId];
    this.isManaged = false;
    this.isReady = false;
    this.isTimerReady = false;
    this.isPlaying = false;
    this.video = null;

    this.appToken = import.meta.env.VITE_TEXTALIVE_APP_TOKEN || TOKEN_PLACEHOLDER;
    this.hasConfiguredToken =
      Boolean(this.appToken) && this.appToken !== TOKEN_PLACEHOLDER;
  }

  initialize() {
    this.player = new Player({
      app: {
        token: this.appToken,
        appName: "Future Idol Memory Stage 2026",
        appAuthor: "Contest entrant",
        parameters: [
          {
            title: "Glow intensity",
            name: "glowIntensity",
            className: "Slider",
            params: [0, 100],
            initialValue: 72,
          },
          {
            title: "Archive density",
            name: "archiveDensity",
            className: "Slider",
            params: [0, 100],
            initialValue: 58,
          },
        ],
      },
      mediaElement: this.mediaElement,
      mediaBannerPosition: "bottom",
      throttleInterval: 100,
      lyricsFetchTimeout: 15000,
      vocalAmplitudeEnabled: true,
      valenceArousalEnabled: true,
    });

    this.player.wait = 33;

    this.player.addListener({
      onAppLoad: (_app, error) => {
        if (error) {
          this.emit("status", {
            tone: "error",
            text: "TextAlive app token rejected",
            detail: error,
          });
        }
      },
      onAppReady: (app) => {
        this.isManaged = Boolean(app.managed);
        this.emit("appReady", app);

        if (!this.hasConfiguredToken) {
          this.emit("status", {
            tone: "warning",
            text: "Set VITE_TEXTALIVE_APP_TOKEN",
          });
          return;
        }

        if (!app.songUrl) {
          this.loadSong(this.selectedSongId);
        } else {
          this.emit("status", { tone: "loading", text: "Loading host track" });
        }
      },
      onAppMediaChange: (songUrl, videoPromise) => {
        const matchedSong = findSongByUrl(songUrl);
        if (matchedSong) {
          this.currentSong = matchedSong;
          this.selectedSongId = matchedSong.id;
        }
        this.isReady = false;
        this.isTimerReady = false;
        this.isPlaying = false;
        this.video = null;
        this.emit("songLoading", this.currentSong);
        if (videoPromise) {
          videoPromise.catch((error) => {
            this.emit("status", {
              tone: "error",
              text: "Unable to load host track",
              detail: error?.message,
            });
          });
        }
      },
      onVideoReady: (video) => {
        this.video = video;
        this.isReady = true;
        this.emit("videoReady", {
          video,
          song: this.currentSong,
          songData: this.player.data?.song,
        });
        this.emit("status", {
          tone: "ready",
          text: this.currentSong ? this.currentSong.title : "Track ready",
        });
      },
      onTimerReady: () => {
        this.isTimerReady = true;
        this.emit("timerReady");
      },
      onPlay: () => {
        this.isPlaying = true;
        this.emit("play");
      },
      onPause: () => {
        this.isPlaying = false;
        this.emit("pause");
      },
      onStop: () => {
        this.isPlaying = false;
        this.emit("stop");
      },
      onMediaSeek: (position) => {
        this.emit("seek", position);
      },
      onAppParameterUpdate: (name, value) => {
        this.emit("parameter", { name, value });
      },
    });
  }

  async loadSong(songId) {
    if (this.isManaged || !this.hasConfiguredToken) return;
    const song = SONG_BY_ID[songId] || SONG_BY_ID[DEFAULT_SONG_ID];
    this.selectedSongId = song.id;
    this.currentSong = song;
    this.isReady = false;
    this.isTimerReady = false;
    this.video = null;
    this.emit("songLoading", song);
    this.emit("status", { tone: "loading", text: `Loading ${song.title}` });

    try {
      if (this.player.isPlaying) this.player.requestPause();
      await this.player.createFromSongUrl(song.songUrl, { video: song.video });
    } catch (error) {
      this.emit("status", {
        tone: "error",
        text: "Unable to load track",
        detail: error?.message,
      });
    }
  }

  play() {
    if (!this.isTimerReady) return false;
    return this.player.requestPlay();
  }

  pause() {
    if (!this.isTimerReady) return false;
    return this.player.requestPause();
  }

  getPosition() {
    if (!this.isReady || !this.isTimerReady || !this.player?.timer) return 0;
    return this.player.timer.position || 0;
  }

  getDuration() {
    return this.video?.duration || 0;
  }

  getReactiveMetrics(position) {
    if (!this.player || !this.video) {
      return { vocal: 0, valence: 0, arousal: 0 };
    }

    const maxVocal = this.player.getMaxVocalAmplitude?.() || 1;
    const rawVocal = this.player.getVocalAmplitude?.(position) || 0;
    const va = this.player.getValenceArousal?.(position) || { v: 0, a: 0 };

    return {
      vocal: maxVocal > 0 ? Math.min(rawVocal / maxVocal, 1) : 0,
      valence: va.v || 0,
      arousal: va.a || 0,
    };
  }
}
